import { useCallback, useEffect, useState } from 'react';
import { Check, Lock, Pencil, Plus, Save, Trash2 } from 'lucide-react';

import { attendanceApi } from '../../services/api';
import type { AssessmentSheetUpdate, GradeSheet, GradeSheetStudent } from '../../types';
import { cn } from '../../utils/cn';
import { formatNumber, isLockedByDefault, parseNumber, readScore } from '../../utils/gradeSheet';
import { Button, EmptyState, Input, LoadingState, useToast } from '../ui';
import { focusNextRow, lockLabel, SheetNotice, useGradeSheet } from './gradeSheetShared';

const cellInput = 'h-9 w-full min-w-[3.5rem] rounded border border-ink-300 bg-white px-2 text-center text-sm tabular-nums text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-200 disabled:cursor-not-allowed disabled:bg-ink-100 disabled:text-ink-400';

// scores[assessmentId][studentId] = chuỗi đang nhập.
type ScoreDrafts = Record<string, Record<string, string>>;
type HeaderDraft = { name: string; weight: string };

interface AssessmentSheetProps {
    attendanceId: string;
    canEdit: boolean;
    hasUnsavedAttendance?: boolean;
    refreshKey?: number;
}

function draftsFrom(sheet: GradeSheet, keep: ScoreDrafts = {}): ScoreDrafts {
    return Object.fromEntries(sheet.assessments.map(assessment => [
        assessment.id,
        keep[assessment.id] ?? Object.fromEntries(sheet.students.map(student => [student.id, formatNumber(assessment.scores[student.id])])),
    ]));
}

function readWeight(text: string) {
    const weight = parseNumber(text);
    return weight !== null && !Number.isNaN(weight) && weight > 0 && weight <= 10 ? weight : null;
}

export function AssessmentSheet({ attendanceId, canEdit, hasUnsavedAttendance, refreshKey }: AssessmentSheetProps) {
    const { showToast } = useToast();
    const [scores, setScores] = useState<ScoreDrafts>({});
    const [headers, setHeaders] = useState<Record<string, HeaderDraft>>({});
    const [editingHeader, setEditingHeader] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);
    const [newTest, setNewTest] = useState<HeaderDraft>({ name: '', weight: '1' });
    const [busy, setBusy] = useState<'save' | 'add' | 'delete' | null>(null);
    const [dirty, setDirty] = useState(false);

    const hasScore = useCallback(
        (student: GradeSheetStudent, current: GradeSheet) => current.assessments.some(assessment => assessment.scores[student.id] !== undefined),
        [],
    );
    const { sheet, setSheet, loading, unlocked, setUnlocked } = useGradeSheet(attendanceId, hasScore, refreshKey);

    useEffect(() => {
        if (!sheet) return;
        setScores(current => draftsFrom(sheet, dirty ? current : {}));
        setHeaders(Object.fromEntries(sheet.assessments.map(assessment => [
            assessment.id,
            { name: assessment.name, weight: formatNumber(assessment.weight) },
        ])));
        // Giữ điểm đang nhập khi chỉ thêm hoặc xoá một bài.
    }, [sheet]);

    if (loading) return <LoadingState title="Đang tải điểm kiểm tra..." />;
    if (!sheet) return <EmptyState title="Không tải được điểm kiểm tra" description="Thử đóng và mở lại buổi học." />;

    const updateScore = (assessmentId: string, studentId: string, value: string) => {
        setScores(current => ({ ...current, [assessmentId]: { ...current[assessmentId], [studentId]: value } }));
        setDirty(true);
    };

    const setLocked = (student: GradeSheetStudent, locked: boolean) => {
        setUnlocked(current => {
            const next = new Set(current);
            if (locked) next.delete(student.id); else next.add(student.id);
            return next;
        });
        if (locked) {
            setScores(current => Object.fromEntries(Object.entries(current).map(([id, row]) => [id, { ...row, [student.id]: '' }])));
        }
        setDirty(true);
    };

    const addTest = async () => {
        const weight = readWeight(newTest.weight);
        if (!newTest.name.trim()) {
            showToast('Nhập tên bài kiểm tra.', 'error');
            return;
        }
        if (weight === null) {
            showToast('Hệ số phải lớn hơn 0 và không quá 10.', 'error');
            return;
        }
        setBusy('add');
        try {
            const response = await attendanceApi.createAssessment(attendanceId, { name: newTest.name.trim(), weight });
            setSheet(response.data.data);
            setAdding(false);
            setNewTest({ name: '', weight: '1' });
        } catch (error: any) {
            showToast(error?.message || 'Không thêm được bài kiểm tra.', 'error');
        } finally {
            setBusy(null);
        }
    };

    const deleteTest = async (assessmentId: string) => {
        setBusy('delete');
        try {
            const response = await attendanceApi.deleteAssessment(attendanceId, assessmentId);
            setScores(current => {
                const { [assessmentId]: _removed, ...rest } = current;
                return rest;
            });
            setSheet(response.data.data);
            setConfirmDelete(null);
            showToast('Đã xoá bài kiểm tra.', 'success');
        } catch (error: any) {
            showToast(error?.message || 'Không xoá được bài kiểm tra.', 'error');
        } finally {
            setBusy(null);
        }
    };

    const save = async () => {
        const updates: AssessmentSheetUpdate[] = [];
        for (const assessment of sheet.assessments) {
            const header = headers[assessment.id];
            const weight = readWeight(header?.weight ?? '');
            if (!header?.name.trim() || weight === null) {
                showToast(`Bài "${assessment.name}": cần tên và hệ số từ 0 đến 10.`, 'error');
                return;
            }
            const update: AssessmentSheetUpdate = { id: assessment.id, scores: [] };
            if (header.name.trim() !== assessment.name) update.name = header.name.trim();
            if (weight !== assessment.weight) update.weight = weight;
            for (const student of sheet.students) {
                const text = unlocked.has(student.id) ? scores[assessment.id]?.[student.id] ?? '' : '';
                const result = readScore(text);
                if (result.kind === 'error') {
                    showToast(`${student.name} – ${assessment.name}: ${result.message}`, 'error');
                    return;
                }
                const saved = assessment.scores[student.id];
                if (result.kind === 'ok' && result.score !== saved) update.scores.push({ studentId: student.id, score: result.score });
                if (result.kind === 'empty' && saved !== undefined) update.scores.push({ studentId: student.id, score: null });
            }
            if (update.scores.length || update.name !== undefined || update.weight !== undefined) updates.push(update);
        }
        if (updates.length === 0) {
            setDirty(false);
            showToast('Không có thay đổi nào.', 'info');
            return;
        }
        setBusy('save');
        try {
            const response = await attendanceApi.saveAssessments(attendanceId, updates);
            setDirty(false);
            setScores(draftsFrom(response.data.data));
            setSheet(response.data.data);
            setEditingHeader(null);
            showToast('Đã lưu điểm kiểm tra.', 'success');
        } catch (error: any) {
            showToast(error?.message || 'Lưu điểm kiểm tra thất bại.', 'error');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-4">
            {hasUnsavedAttendance ? (
                <SheetNotice>Điểm danh đang có thay đổi chưa lưu. Lưu điểm danh trước để danh sách ở đây đúng trạng thái.</SheetNotice>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-ink-600">Học sinh vắng không tính vào điểm trung bình.</p>
                {canEdit ? (
                    <div className="flex flex-wrap gap-2">
                        {!adding ? (
                            <Button variant="secondary" onClick={() => setAdding(true)} icon={<Plus className="h-4 w-4" />}>
                                Thêm bài kiểm tra
                            </Button>
                        ) : null}
                        {sheet.assessments.length ? (
                            <Button onClick={save} loading={busy === 'save'} disabled={!dirty || busy !== null} icon={<Save className="h-4 w-4" />}>
                                Lưu điểm
                            </Button>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {adding ? (
                <form
                    className="flex flex-wrap items-end gap-3 rounded-lg border border-ink-200 bg-ink-50 p-3"
                    onSubmit={event => { event.preventDefault(); void addTest(); }}
                >
                    <label className="min-w-[12rem] flex-1 text-sm">
                        <span className="mb-1 block font-medium text-ink-700">Tên bài kiểm tra</span>
                        <Input
                            autoFocus
                            value={newTest.name}
                            onChange={event => setNewTest(current => ({ ...current, name: event.target.value }))}
                            placeholder="Kiểm tra 15 phút – Unit 3"
                        />
                    </label>
                    <label className="w-24 text-sm">
                        <span className="mb-1 block font-medium text-ink-700">Hệ số</span>
                        <Input
                            inputMode="decimal"
                            value={newTest.weight}
                            onChange={event => setNewTest(current => ({ ...current, weight: event.target.value }))}
                        />
                    </label>
                    <div className="flex gap-2">
                        <Button type="submit" loading={busy === 'add'}>Thêm</Button>
                        <Button variant="ghost" onClick={() => setAdding(false)}>Huỷ</Button>
                    </div>
                </form>
            ) : null}

            {sheet.assessments.length === 0 ? (
                <EmptyState
                    title="Buổi này chưa có bài kiểm tra"
                    description={canEdit ? 'Bấm "Thêm bài kiểm tra", nhập tên và hệ số để bắt đầu nhập điểm.' : undefined}
                />
            ) : (
                <div className="overflow-x-auto rounded-lg border border-ink-200">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-ink-200 bg-ink-50 text-left text-xs font-medium text-ink-600">
                                <th scope="col" className="sticky left-0 z-10 bg-ink-50 px-3 py-2">Học sinh</th>
                                {sheet.assessments.map(assessment => (
                                    <th key={assessment.id} scope="col" className="w-36 min-w-[7.5rem] px-2 py-2 align-top">
                                        {editingHeader === assessment.id ? (
                                            <div className="space-y-1">
                                                <input
                                                    aria-label="Tên bài kiểm tra"
                                                    value={headers[assessment.id]?.name ?? ''}
                                                    onChange={event => {
                                                        setHeaders(current => ({ ...current, [assessment.id]: { ...current[assessment.id], name: event.target.value } }));
                                                        setDirty(true);
                                                    }}
                                                    className="h-8 w-full rounded border border-ink-300 px-2 text-xs font-normal text-ink-900"
                                                />
                                                <div className="flex items-center gap-1">
                                                    <span className="font-normal">Hệ số</span>
                                                    <input
                                                        aria-label="Hệ số"
                                                        inputMode="decimal"
                                                        value={headers[assessment.id]?.weight ?? ''}
                                                        onChange={event => {
                                                            setHeaders(current => ({ ...current, [assessment.id]: { ...current[assessment.id], weight: event.target.value } }));
                                                            setDirty(true);
                                                        }}
                                                        className="h-8 w-14 rounded border border-ink-300 px-2 text-center text-xs font-normal text-ink-900"
                                                    />
                                                    <button type="button" onClick={() => setEditingHeader(null)} aria-label="Xong" className="flex h-8 w-8 items-center justify-center rounded text-ink-600 hover:bg-ink-100">
                                                        <Check className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : confirmDelete === assessment.id ? (
                                            <div className="space-y-1 text-center">
                                                <p className="font-normal text-danger-700">Xoá bài và toàn bộ điểm?</p>
                                                <div className="flex justify-center gap-1">
                                                    <button type="button" onClick={() => void deleteTest(assessment.id)} disabled={busy !== null} className="rounded bg-danger-600 px-2 py-1 text-xs font-medium text-white hover:bg-danger-700 disabled:opacity-50">
                                                        Xoá
                                                    </button>
                                                    <button type="button" onClick={() => setConfirmDelete(null)} className="rounded px-2 py-1 text-xs text-ink-600 hover:bg-ink-100">
                                                        Huỷ
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-start justify-between gap-1">
                                                <div className="min-w-0 text-center flex-1">
                                                    <p className="font-semibold text-ink-900 break-words">{headers[assessment.id]?.name || assessment.name}</p>
                                                    <p className="font-normal text-ink-500">Hệ số {headers[assessment.id]?.weight || formatNumber(assessment.weight)}</p>
                                                </div>
                                                {canEdit ? (
                                                    <div className="flex shrink-0 flex-col">
                                                        <button type="button" onClick={() => setEditingHeader(assessment.id)} aria-label={`Sửa ${assessment.name}`} className="flex h-6 w-6 items-center justify-center rounded text-ink-400 hover:bg-ink-100 hover:text-ink-700">
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button type="button" onClick={() => setConfirmDelete(assessment.id)} aria-label={`Xoá ${assessment.name}`} className="flex h-6 w-6 items-center justify-center rounded text-ink-400 hover:bg-danger-50 hover:text-danger-700">
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                ) : null}
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-ink-100">
                            {sheet.students.map((student, rowIndex) => {
                                const isUnlocked = unlocked.has(student.id);
                                return (
                                    <tr key={student.id} className={cn(!isUnlocked && 'bg-ink-50/60')}>
                                        <td className={cn('sticky left-0 z-10 px-3 py-2', isUnlocked ? 'bg-white' : 'bg-ink-50')}>
                                            <span className={cn('block font-medium', isUnlocked ? 'text-ink-900' : 'text-ink-500')}>{student.name}</span>
                                            {isUnlocked && isLockedByDefault(student) ? (
                                                <span className="flex flex-wrap items-center gap-x-1 text-xs text-warning-700">
                                                    {lockLabel(student)} · làm bù
                                                    {canEdit ? (
                                                        <button type="button" onClick={() => setLocked(student, true)} className="rounded px-1 text-ink-500 hover:text-ink-900 hover:underline">
                                                            Khoá lại
                                                        </button>
                                                    ) : null}
                                                </span>
                                            ) : null}
                                        </td>
                                        {isUnlocked ? sheet.assessments.map(assessment => {
                                            const text = scores[assessment.id]?.[student.id] ?? '';
                                            const invalid = readScore(text).kind === 'error';
                                            return (
                                                <td key={assessment.id} className="px-2 py-1.5">
                                                    <input
                                                        aria-label={`Điểm ${assessment.name} – ${student.name}`}
                                                        inputMode="decimal"
                                                        enterKeyHint="next"
                                                        data-sheet-row={rowIndex}
                                                        data-sheet-col={assessment.id}
                                                        value={text}
                                                        disabled={!canEdit}
                                                        onChange={event => updateScore(assessment.id, student.id, event.target.value)}
                                                        onKeyDown={event => { if (event.key === 'Enter') focusNextRow(event.currentTarget); }}
                                                        className={cn(cellInput, invalid && 'border-danger-400 text-danger-700')}
                                                    />
                                                </td>
                                            );
                                        }) : (
                                            <td colSpan={sheet.assessments.length} className="px-3 py-2">
                                                <div className="flex flex-wrap items-center gap-2 text-sm text-ink-500">
                                                    <Lock className="h-3.5 w-3.5" />
                                                    <span>{lockLabel(student)}</span>
                                                    {canEdit && student.status !== null ? (
                                                        <button type="button" onClick={() => setLocked(student, false)} className="rounded px-2 py-1 text-sm font-medium text-academy-700 hover:bg-academy-50">
                                                            Bật lại
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
